import React from "react-native";
import { NIL } from "uuid";
import {
    GQFiskalyOrganizationInput,
    GQIntegrationQuery,
    useFiskalyOrganizationGoLiveMutation,
    useFiskalyOrganizationLazyQuery,
    useFiskalyOrganizationUpsertMutation,
    useIntegrationSettingsLazyQuery,
} from "graphql-sdk";
import { useConfirm, useForm } from "@venuepos/react-common";
import { FiskalyOrganizationForm } from "./form";
import { FiskalySettings } from "lib";
import { useCallback, useEffect } from "react";
import { useHandleMutationError } from "../../../../hooks";
import { useTranslation } from "locales";

export function FiskalyOrganizationTab({
    integration,
}: {
    integration: GQIntegrationQuery["integration"];
}) {
    const [t] = useTranslation();
    const confirm = useConfirm();
    const [getIntegrationSettings, integrationSettingsResult] =
        useIntegrationSettingsLazyQuery({
            variables: {
                integrationId: integration.id,
            },
        });

    const fiskalySettings = integrationSettingsResult?.data
        ?.integrationSettings as FiskalySettings;

    const [getFiskalyOrganization, fiskalyOrganization] =
        useFiskalyOrganizationLazyQuery();

    const form = useForm<GQFiskalyOrganizationInput>(
        {
            name: { required: true },
            address: { required: true },
            zip: {
                required: true,
                minLength: { length: 5 },
                maxLength: { length: 5 },
            },
            town: { required: true },
            countryCode: { required: true },
        },
        null
    );

    const [{ values }, { setDefaultValues }] = form;

    const [upsertFiskalyOrganization] = useFiskalyOrganizationUpsertMutation();
    const { handleMutationError } = useHandleMutationError();

    const save = useCallback(async () => {
        if (
            !integrationSettingsResult ||
            integrationSettingsResult.loading ||
            !integrationSettingsResult.data
        ) {
            return;
        }

        await handleMutationError(() => {
            upsertFiskalyOrganization({
                variables: {
                    managedOrganization: {
                        id: (
                            integrationSettingsResult.data
                                .integrationSettings as FiskalySettings
                        ).organizationId,
                        ...values,
                    },
                },
            });
        }, t("backoffice.integration.fiskaly.saved_organization", "Organization saved"));
    }, [
        handleMutationError,
        integrationSettingsResult,
        t,
        upsertFiskalyOrganization,
        values,
    ]);

    const [goLive] = useFiskalyOrganizationGoLiveMutation();

    const handleGoLive = useCallback(async () => {
        if (
            !(await confirm(
                t(
                    "backoffice.integration.fiskaly.go_live_header",
                    "Ready to go live?"
                ),
                t(
                    "backoffice.integration.fiskaly.go_live_bodytext",
                    "Are you ready to go live with this organization?\n\nThis cannot be undone."
                )
            ))
        ) {
            return;
        }

        await handleMutationError(async () => {
            goLive({ variables: { integrationId: integration.id } });
        }, t("backoffice.integration.fiskaly.go_live_success", "This organization is now live."));
    }, [confirm, goLive, handleMutationError, integration.id, t]);

    useEffect(() => {
        if (!integration || !fiskalySettings) {
            return;
        }

        if (
            !fiskalyOrganization.called &&
            fiskalySettings.organizationId &&
            fiskalySettings.organizationId !== NIL
        ) {
            getFiskalyOrganization({
                variables: {
                    id: fiskalySettings.organizationId,
                },
            });
        }

        // If there is no organization saved for this integration, then use the default values and let the user create a new Organization
        if (
            fiskalySettings.organizationId &&
            fiskalySettings.organizationId === NIL
        ) {
            setDefaultValues({
                id: fiskalySettings.organizationId,
                name: "",
                displayName: "",
                address: "",
                zip: "",
                town: "",
                state: "",
                countryCode: "DEU",
                taxNumber: "",
                vatId: "",
                economyId: "",
            });
        }
    }, [
        fiskalyOrganization.called,
        fiskalySettings,
        getFiskalyOrganization,
        integration,
        setDefaultValues,
    ]);

    useEffect(() => {
        if (!integration) {
            return;
        }

        if (!fiskalyOrganization?.data) {
            return;
        }

        if (values) {
            return;
        }

        const d = fiskalyOrganization.data.fiskalyOrganization;

        setDefaultValues({
            id: d.id,
            name: d.name,
            displayName: d.displayName,
            address: d.address,
            town: d.town,
            zip: d.zip,
            state: d.state,
            countryCode: d.countryCode,
            taxNumber: d.taxNumber,
            vatId: d.vatId,
            economyId: d.economyId,
        });
    }, [fiskalyOrganization, integration, setDefaultValues, values]);

    useEffect(() => {
        if (!integration?.id) {
            return;
        }

        getIntegrationSettings({
            variables: { integrationId: integration.id },
        });
    }, [getIntegrationSettings, integration?.id]);

    return (
        <FiskalyOrganizationForm
            form={form}
            onSubmit={async () => {
                await save();
            }}
            onGoLive={async () => {
                handleGoLive();
            }}
        />
    );
}
